import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ActiveOrderLine,
  Cart,
  CheckoutNotes,
  CustomerCart,
  OrderPricelist,
  OrderRootState,
  ShippingAddress,
} from './order-model';

export const ORDER_FETAURE_KEY = 'orderData';

interface OrderDataState {
  orderStatus: string;
  cart?: Cart;
  shippingAddress?: ShippingAddress;
  checkoutNotes?: CheckoutNotes;
  orderPricelist?: OrderPricelist;
  orderDetailsData: {
    data: Record<string, string>;
    includePaymentLink: boolean;
  };
}

export const initialOrderDataState: OrderDataState = {
  orderStatus: '',
  orderDetailsData: {
    data: {},
    includePaymentLink: false,
  },
};

export const orderDataSlice = createSlice({
  name: ORDER_FETAURE_KEY,
  initialState: initialOrderDataState,
  reducers: {
    updateOrderStatus(state, action: PayloadAction<string>) {
      state.orderStatus = action.payload;
    },
    updateShippingAddress(state, action: PayloadAction<ShippingAddress>) {
      state.shippingAddress = action.payload;
    },
    updateCheckoutNotes(state, action: PayloadAction<CheckoutNotes>) {
      state.checkoutNotes = action.payload;
    },
    updateOrderPricelist(state, action: PayloadAction<OrderPricelist>) {
      state.orderPricelist = action.payload;
    },
    removeOrderPricelist(state) {
      state.orderPricelist = undefined;
    },
    addItemToCart(
      state,
      action: PayloadAction<{
        customerId: string;
        line: ActiveOrderLine;
        customerName: string;
        companyName: string;
        customerCode?: string;
        phone?: string;
      }>
    ) {
      const {
        customerId,
        line,
        customerName,
        companyName,
        customerCode,
        phone,
      } = action.payload;

      // Ensure state.cart exists
      if (!state.cart) {
        state.cart = {};
      }

      state.cart[customerId] = {
        ...state.cart[customerId],
        customerName: customerName,
        companyName: companyName,
        phone: phone,
        customerCode: customerCode,
      };

      state.cart[customerId].lines = [
        ...(state.cart[customerId]?.lines || []),
        line,
      ];
    },
    addBulkItemsToCart(
      state,
      action: PayloadAction<
        Partial<CustomerCart> & {
          customerId: string;
        }
      >
    ) {
      const { customerId, poFileId, lines, orderAmount, orderId, tax } =
        action.payload;

      // Ensure state.cart exists
      if (!state.cart) {
        state.cart = {};
      }

      state.cart[customerId] = {
        ...state.cart[customerId],
        poFileId,
        orderAmount,
        orderId,
        tax,
      };

      if (lines) {
        state.cart[customerId].lines = [...lines];
      }
    },
    removeItemFromCart(
      state,
      action: PayloadAction<{ customerId: string; line: ActiveOrderLine }>
    ) {
      const { customerId, line } = action.payload;

      if (!state.cart || !state.cart[customerId]) return;

      // Filter out the line based on id (or another unique key)
      state.cart[customerId].lines = state?.cart?.[customerId]?.lines?.filter(
        (l) => l?.productVariantId !== line?.productVariantId
      );

      // if customer has zero items, remove the object
      if (state.cart[customerId].lines.length === 0) {
        delete state.cart[customerId];
      }

      // if cart becomes empty entirely
      if (Object.keys(state.cart).length === 0) {
        state.cart = undefined;
      }
    },
    updateItemInCart(
      state,
      action: PayloadAction<{ customerId: string; line: ActiveOrderLine }>
    ) {
      const { customerId, line } = action.payload;

      if (!state.cart || !state.cart[customerId]) return;

      const lines = state.cart[customerId].lines;

      // Find index of the item to update
      const index = lines?.findIndex(
        (l) => l?.productVariantId === line?.productVariantId
      );
      if (index === -1) return;

      // Replace the line
      state.cart[customerId].lines[index] = {
        ...state.cart[customerId].lines[index],
        ...line,
      };
    },
    updateOrderDetailsData: (
      state,
      action: PayloadAction<{
        data: Record<string, string>;
        includePaymentLink: boolean;
      }>
    ) => {
      state.orderDetailsData = action.payload;
    },
    clearOrderDetailsData: (state) => {
      state.orderDetailsData = {
        data: {},
        includePaymentLink: false,
      };
    },
    clearCartByCustomer(state, action: PayloadAction<string>) {
      if (!state.cart || !state.cart[action.payload]) return;

      delete state.cart[action.payload];
    },
    resetOrderCart(state) {
      state.cart = {};
    },
  },
});

export const orderDataReducer = orderDataSlice.reducer;

export const {
  updateOrderStatus,
  updateShippingAddress,
  updateCheckoutNotes,
  addItemToCart,
  removeItemFromCart,
  updateItemInCart,
  clearCartByCustomer,
  addBulkItemsToCart,
  updateOrderPricelist,
  removeOrderPricelist,
  resetOrderCart,
  updateOrderDetailsData,
  clearOrderDetailsData,
} = orderDataSlice.actions;

export const getOrderDataSliceState = (
  rootState: OrderRootState
): OrderDataState => rootState[ORDER_FETAURE_KEY];

export const getOrderStatus = createSelector(
  getOrderDataSliceState,
  (state) => state.orderStatus
);

export const getShippingAddress = createSelector(
  getOrderDataSliceState,
  (state) => state.shippingAddress
);

export const getCheckoutNotes = createSelector(
  getOrderDataSliceState,
  (state) => state.checkoutNotes
);

export const getOrderPricelist = createSelector(
  getOrderDataSliceState,
  (state) => state.orderPricelist
);

export const getAllCustomersCart = createSelector(
  getOrderDataSliceState,
  (state) => state.cart
);

export const getCartByCustomer = createSelector(
  [getOrderDataSliceState, (_, customerId: string) => customerId],
  (orderData, customerId) => orderData?.cart?.[customerId]
);

export const getOrderDetailsData = createSelector(
  getOrderDataSliceState,
  (state) => state.orderDetailsData
);
